import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function KPI() {
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period: new Date().toISOString().slice(0,7), metric: "", target: 0, actual: 0 });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiRow, setAiRow] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get("/kpi").then((r) => setData(r.data));
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);

  const create = async () => {
    try {
      await api.post("/kpi", { ...form, target: Number(form.target), actual: Number(form.actual) });
      toast.success("Đã ghi nhận KPI"); setOpen(false); load();
    } catch { toast.error("Thất bại"); }
  };

  const explainAnomaly = async (row) => {
    setAiRow(row); setAiOpen(true); setAiLoading(true); setAiResult(null);
    try {
      const history = data.filter((d) => d.employee_id === row.employee_id && d.metric === row.metric && d.id !== row.id).map((d) => Number(d.score));
      const padded = history.length > 0 ? history : [row.score * 0.95, row.score * 1.02, row.score * 0.98, row.score];
      const r = await api.post("/ai/kpi-anomaly", {
        metric: row.metric, current: Number(row.score), history: padded, context: `Nhân viên ${row.emp_name}, period ${row.period}`,
      });
      setAiResult(r.data);
    } catch (e) {
      toast.error("AI lỗi: " + (e?.response?.data?.detail || e.message));
      setAiOpen(false);
    } finally { setAiLoading(false); }
  };

  return (
    <div data-testid="page-kpi">
      <PageHeader
        kicker="HR-Ops" title="KPI Tracker" breadcrumb={["HR-Ops", "KPI"]}
        actions={
          <Button onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-kpi">
            <Plus className="w-4 h-4 mr-1.5" /> Ghi nhận KPI
          </Button>
        }
      />
      <div className="p-6 lg:p-8">
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {["Kỳ","NV","Chỉ tiêu","Target","Actual","Score","AI"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.map((k) => {
                const score = Number(k.score);
                const color = score >= 95 ? "bg-emerald-100 text-emerald-900" : score >= 80 ? "bg-amber-100 text-amber-900" : "bg-red-100 text-red-900";
                return (
                  <tr key={k.id} className="hover:bg-stone-50" data-testid={`row-kpi-${k.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{k.period}</td>
                    <td className="px-4 py-2"><span className="font-semibold">{k.emp_name}</span> <span className="text-xs font-mono text-stone-500">{k.emp_code}</span></td>
                    <td className="px-4 py-2">{k.metric}</td>
                    <td className="px-4 py-2 font-mono text-xs">{k.target}</td>
                    <td className="px-4 py-2 font-mono text-xs">{k.actual}</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-sm font-bold font-mono ${color}`}>{score.toFixed(1)}</span></td>
                    <td className="px-4 py-2">
                      <button onClick={() => explainAnomaly(k)} className="text-xs px-2 py-1 bg-amber-50 text-amber-900 rounded-sm hover:bg-amber-100 flex items-center gap-1" data-testid={`btn-explain-${k.id}`}>
                        <Sparkles className="w-3 h-3" /> Phân tích
                      </button>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">Chưa có dữ liệu KPI</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Ghi nhận KPI</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nhân viên *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn nhân viên" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} ({e.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kỳ (YYYY-MM)</Label>
                <Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="h-9 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs">Chỉ tiêu *</Label>
                <Input value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} placeholder="VD: Số bàn phục vụ" className="h-9 rounded-sm mt-1" data-testid="f-kpi-metric" />
              </div>
              <div>
                <Label className="text-xs">Target</Label>
                <Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="h-9 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs">Actual</Label>
                <Input type="number" value={form.actual} onChange={(e) => setForm({ ...form, actual: e.target.value })} className="h-9 rounded-sm mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={create} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-kpi">Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />Phân tích AI
            </DialogTitle>
          </DialogHeader>
          {aiRow && (
            <div className="text-sm space-y-3">
              <div className="bg-stone-50 rounded-sm p-3">
                <div className="text-xs text-stone-500">Chỉ tiêu</div>
                <div className="font-semibold">{aiRow.metric} của {aiRow.emp_name}</div>
                <div className="text-xs text-stone-500 mt-1">Score hiện tại: <span className="font-mono font-bold">{aiRow.score}</span></div>
              </div>
              {aiLoading && (
                <div className="text-center py-6 text-stone-500">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Minimax đang phân tích…
                </div>
              )}
              {aiResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white border border-stone-200 rounded-sm p-2">
                      <div className="text-[10px] uppercase text-stone-500 font-bold">Z-Score</div>
                      <div className="font-mono font-bold text-stone-900">{aiResult.z_score}</div>
                    </div>
                    <div className="bg-white border border-stone-200 rounded-sm p-2">
                      <div className="text-[10px] uppercase text-stone-500 font-bold">Bất thường</div>
                      <div className={`font-bold ${aiResult.is_anomaly ? "text-red-700" : "text-emerald-800"}`}>{aiResult.is_anomaly ? "CÓ" : "KHÔNG"}</div>
                    </div>
                    <div className="bg-white border border-stone-200 rounded-sm p-2">
                      <div className="text-[10px] uppercase text-stone-500 font-bold">Tin cậy</div>
                      <div className="font-bold capitalize">{aiResult.confidence}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">Phân tích</div>
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm whitespace-pre-wrap">{aiResult.explanation || "(không có phản hồi)"}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
